import { useState, useEffect } from "react";
import { T } from "../../theme";
import { apiCall, apiCallEx, errorText } from "../../apiClient";
import { Btn, Inp, Card, Badge, SectionTitle } from "../primitives";

// Mirrors backend/src/utils/slugRule.js. The slug lands in a URL path, and a
// customer gateway decodes before matching its rules, so a dangerous character
// has to be kept out of the VALUE — encoding it is not enough. The server
// enforces the same rule and is the authority; this only stops the admin typing
// something that would be refused.
const SLUG_RULE_TEXT = "Lowercase letters, numbers, hyphens and underscores only, starting with a letter or number.";
const normalizeSlug = (text: string) =>
  String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/[-_]{2,}/g, "-")
    .replace(/^[-_]+/, "")
    .slice(0, 120);

export const PlaybookManager = () => {
  const [playbooks, setPlaybooks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState<Record<string, any>>({ name: '', slug: '', description: '', contract_types: [], content: '', ai_provider: 'anthropic' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  const load = async () => { setLoading(true); const r = await apiCall('/playbooks'); if (r?.playbooks) setPlaybooks(r.playbooks); setLoading(false); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.content) return;
    setSaving(true); setSaveError(''); setSaveSuccess('');
    const slug = normalizeSlug(form.slug || form.name);
    try {
      // apiCallEx, not apiCall. `apiCall` returns null on every non-2xx and
      // never throws, so `r?.error` was always undefined and the throw below was
      // dead code: a REJECTED playbook save fell straight through to the green
      // "Saved ... at 14:32" banner. The admin was told, with a timestamp, that
      // content had been stored that had not been.
      const r = editing && editing !== 'new'
        ? await apiCallEx(`/playbooks/${editing}`, { method: 'PUT', body: JSON.stringify({ ...form }) }, 'return')
        : await apiCallEx('/playbooks', { method: 'POST', body: JSON.stringify({ ...form, slug }) }, 'return');
      if (!r.ok) throw new Error(errorText(r, 'The playbook was not saved.'));
      setSaveSuccess(`Saved ${form.name} (${form.content.length.toLocaleString()} chars) at ${new Date().toLocaleTimeString()}`);
      setEditing(null);
      setForm({ name: '', slug: '', description: '', contract_types: [], content: '', ai_provider: 'anthropic' });
      await load();
    } catch (e: any) {
      setSaveError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ textAlign: "center", padding: 20, color: T.txM, fontSize: 12 }}>Loading...</div>;

  return (
    <div>
      <SectionTitle sub="Manage contract review playbooks">Playbooks</SectionTitle>
      {saveSuccess && <div style={{ padding: "8px 12px", backgroundColor: T.grnS, color: T.grn, borderRadius: 5, marginBottom: 10, fontSize: 12 }}>{saveSuccess}</div>}
      {saveError && <div style={{ padding: "8px 12px", backgroundColor: T.redS, color: T.red, borderRadius: 5, marginBottom: 10, fontSize: 12 }}>{saveError}</div>}
      {!editing && playbooks.map((p: any) => (
        <Card key={p.id} style={{ marginBottom: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.tx }}>{p.name} <span style={{ fontSize: 10, color: T.txD, fontWeight: 400 }}>· {p.slug}</span></div>
              <div style={{ fontSize: 11, color: T.txD }}>
                {p.description ? `${p.description} · ` : ''}
                {(p.contract_types || []).join(', ') || 'no types'} · {(p.content || '').length.toLocaleString()} chars
                {p.updated_at ? ` · updated ${new Date(p.updated_at).toLocaleDateString()}` : ''}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <Badge c={p.ai_provider === 'harvey' ? T.prp : p.ai_provider === 'auto' ? T.cyn : T.acc} b={p.ai_provider === 'harvey' ? T.prpS : p.ai_provider === 'auto' ? T.cynS : T.accS}>{p.ai_provider === 'harvey' ? 'Harvey' : p.ai_provider === 'auto' ? 'Auto' : 'Anthropic'}</Badge>
              <Badge c={p.is_active ? T.grn : T.txD} b={p.is_active ? T.grnS : T.bg4}>{p.is_active ? 'Active' : 'Inactive'}</Badge>
              <Btn v="secondary" sz="sm" onClick={() => { setEditing(p.id); setForm({ name: p.name, slug: p.slug, description: p.description || '', contract_types: p.contract_types || [], content: p.content || '', ai_provider: p.ai_provider || 'anthropic' }); apiCall(`/playbooks/${encodeURIComponent(p.slug)}`).then(r => { if (r?.playbook?.content) setForm(f => ({ ...f, content: r.playbook.content })); }); }}>Edit</Btn>
              <Btn v="secondary" sz="sm" onClick={async () => { const r = await apiCallEx(`/playbooks/${p.id}`, { method: 'PUT', body: JSON.stringify({ is_active: !p.is_active }) }, 'return'); if (!r.ok) { setSaveError(errorText(r, `${p.name} was not changed.`)); return; } setSaveError(''); load(); }}>{p.is_active ? 'Deactivate' : 'Activate'}</Btn>
            </div>
          </div>
        </Card>
      ))}
      {(editing || !playbooks.length) ? null : <Btn v="primary" sz="sm" onClick={() => setEditing('new')} style={{ marginTop: 8 }}>+ Add Playbook</Btn>}
      {editing && (
        <Card style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>{editing === 'new' ? 'New Playbook' : 'Edit Playbook'}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <Inp label="Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v, slug: editing === 'new' ? normalizeSlug(v) : f.slug }))} />
            <Inp label="Slug" value={form.slug} onChange={v => setForm(f => ({ ...f, slug: normalizeSlug(v) }))} />
            <div style={{ fontSize: 10, color: T.txD, marginTop: -4 }}>{SLUG_RULE_TEXT}</div>
          </div>
          <Inp label="Description" value={form.description} onChange={v => setForm(f => ({ ...f, description: v }))} />
          <Inp label="Contract Types (comma-separated)" value={(form.contract_types || []).join(', ')} onChange={v => setForm(f => ({ ...f, contract_types: v.split(',').map(s => s.trim()).filter(Boolean) }))} />
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: T.txD, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.04em' }}>AI Provider</label>
            <select value={form.ai_provider || 'anthropic'} onChange={e => setForm(f => ({ ...f, ai_provider: e.target.value }))} style={{ width: '100%', padding: '10px 12px', border: `1px solid ${T.brd}`, borderRadius: 6, fontSize: 13, backgroundColor: T.bg3, color: T.tx, fontFamily: 'inherit', boxSizing: 'border-box' }}>
              <option value="anthropic">Anthropic</option>
              <option value="harvey">Harvey AI</option>
              <option value="auto">Auto (Harvey if available, Anthropic fallback)</option>
            </select>
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: T.txD, marginBottom: 5, textTransform: "uppercase", letterSpacing: ".04em" }}>Content</label>
            <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} rows={15} style={{ width: "100%", minHeight: 300, padding: "10px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, fontFamily: "monospace", resize: "vertical", boxSizing: "border-box" }} />
          </div>
          <div style={{ fontSize: 10, color: T.txD, textAlign: 'right', marginTop: 4 }}>{(form.content || '').length.toLocaleString()} chars</div>
          {saveError && <div style={{ padding: "8px 12px", backgroundColor: T.redS, color: T.red, borderRadius: 5, marginTop: 12, fontSize: 12 }}>{saveError}</div>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
            <Btn v="secondary" sz="sm" onClick={() => { setEditing(null); setSaveError(''); setForm({ name: '', slug: '', description: '', contract_types: [], content: '', ai_provider: 'anthropic' }); }} disabled={saving}>Cancel</Btn>
            <Btn v="primary" sz="sm" onClick={() => { save(); }} disabled={saving || !form.name || !form.content}>{saving ? 'Saving...' : 'Save Playbook'}</Btn>
          </div>
        </Card>
      )}
    </div>
  );
};
